import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { documentsApi } from '../services/documents';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout, isSpedflowAdmin, isTenantAdmin, isAdvocate, isClient } = useAuth();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  // Load pending document count for advocates
  useEffect(() => {
    if (isAdvocate || isTenantAdmin) {
      loadPendingCount();
    }
  }, [isAdvocate, isTenantAdmin]);

  const loadPendingCount = async () => {
    try {
      const result = await documentsApi.getPendingDocuments();
      setPendingCount(result.count);
    } catch (err) {
      // Silently fail - badge just won't show
      console.error('Failed to load pending documents count:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <nav style={{
        width: '240px',
        backgroundColor: '#1a237e',
        color: 'white',
        padding: '20px 0',
      }}>
        <div style={{ padding: '0 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h1 style={{ fontSize: '24px', margin: 0 }}>SPEDflow</h1>
          <p style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
            {user?.email}
          </p>
        </div>

        <ul style={{ listStyle: 'none', padding: '20px 0', margin: 0 }}>
          <NavItem to="/dashboard">Dashboard</NavItem>
          
          {isSpedflowAdmin && (
            <>
              <NavItem to="/admin/tenants">Tenants</NavItem>
            </>
          )}

          {isTenantAdmin && (
            <>
              <NavItem to="/tenant/advocates">Advocates</NavItem>
            </>
          )}

          {(isAdvocate || isTenantAdmin) && (
            <>
              <NavItem to="/cases">Cases</NavItem>
            </>
          )}

          {isAdvocate && (
            <>
              <NavItem to="/clients">Clients</NavItem>
              <NavItem to="/tasks">Tasks</NavItem>
              <NavItem to="/documents/pending" badge={pendingCount > 0 ? pendingCount : undefined}>
                Pending Documents
              </NavItem>
            </>
          )}

          {isClient && (
            <>
              <NavItem to="/portal/documents" disabled>Documents (Coming Soon)</NavItem>
              <NavItem to="/portal/messages" disabled>Messages (Coming Soon)</NavItem>
            </>
          )}
        </ul>

        <div style={{ position: 'absolute', bottom: '20px', left: '20px', right: '20px' }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={{ flex: 1, backgroundColor: '#f5f5f5', padding: '24px' }}>
        {children}
      </main>
    </div>
  );
}

function NavItem({ to, children, disabled, badge }: { to: string; children: React.ReactNode; disabled?: boolean; badge?: number }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  if (disabled) {
    return (
      <li style={{ padding: '12px 20px', opacity: 0.5, cursor: 'not-allowed' }}>
        {children}
      </li>
    );
  }

  return (
    <li>
      <Link
        to={to}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          color: 'white',
          textDecoration: 'none',
          backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
          borderLeft: isActive ? '3px solid #fff' : '3px solid transparent',
        }}
      >
        <span>{children}</span>
        {badge !== undefined && badge > 0 && (
          <span
            style={{
              backgroundColor: '#ff9800',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 600,
              minWidth: '20px',
              textAlign: 'center',
            }}
            data-testid="pending-nav-badge"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
    </li>
  );
}
